import type { CityConfig } from "@/pages/city-template/types";

export const redmondConfig: CityConfig = {
  vitalRecordsIcon: "ri-file-text-line",
  vitalRecordsLinks: [
    { icon: "ri-file-text-line", label: "Oregon Vital Records (Birth, Death, Marriage)", desc: "Request certified birth, death, and marriage certificates from Oregon Vital Records.", url: "https://www.oregon.gov/oha/PH/BirthDeathCertificates/Pages/index.aspx" },
    { icon: "ri-government-line", label: "Deschutes County Clerk Vital Records", desc: "Obtain vital records through the Deschutes County Clerk's office in Bend.", url: "https://www.deschutes.org/clerk" },
    { icon: "ri-global-line", label: "VitalChek — Order Vital Records Online", desc: "Official online ordering partner of Oregon Vital Records for certified certificates.", url: "https://www.vitalchek.com/" },
  ],
  city: "Redmond",
  state: "Oregon",
  stateAbbr: "OR",
  slug: "redmond",
  route: "/redmond",
  title: "Emergency Passport Redmond OR | Expedited Passport Deschutes County Oregon",
  metaDescription: "Emergency passport and expedited passport services in Redmond, OR — as fast as 24 hours. Fast Passport Center serves Redmond, Deschutes County, Bend, Sisters, Prineville, and Central Oregon. Registered State Dept. courier. A+ BBB rated. No office visit required. Roberts Field (RDM) airport community served.",
  h1: "Emergency & Expedited Passport Services in Redmond, Oregon",
  heroSubtitle: "Roberts Field (RDM) international flight booked? Last-minute business trip across the Pacific? Outdoor adventure tour to New Zealand and your passport is expired? Fast Passport Center helps Redmond and Central Oregon residents get expedited passports as fast as 24 hours — entirely remotely. The Seattle Passport Agency is 320 miles away, but we serve you without the drive.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Redmond%20Oregon%20high%20desert%20landscape%20with%20Smith%20Rock%20State%20Park%20dramatic%20rock%20formations%20in%20golden%20hour%20light%2C%20Central%20Oregon%20high%20desert%20with%20juniper%20trees%20and%20Cascade%20mountain%20range%20panorama%20including%20Mount%20Jefferson%20and%20Three%20Sisters%20in%20background%2C%20sunny%20clear%20skies%20over%20high%20desert%20plateau%20at%20Roberts%20Field%20RDM%20airport%20region%2C%20classic%20Oregon%20high%20desert%20scenery%2C%20photorealistic%20cinematic%20quality%2C%20no%20text%20no%20people&width=1800&height=700&seq=redmond-or-hero-01&orientation=landscape",
  heroImageAlt: "Redmond Oregon high desert landscape with Smith Rock and Cascade Mountains panorama",
  geoPosition: "44.2726;-121.1739",
  geoPlacename: "Redmond, Oregon",
  lastModified: "2026-06-14",
  regionalAgency: {
    name: "Seattle Passport Agency",
    address: "915 2nd Ave Suite 500, Seattle WA 98174",
    distance: "About 320 miles northwest — approximately 5.5 hours by car via US-97 and I-5",
  },
  nearbyCitiesZips: [
    { name: "Redmond", zips: "97756" },
    { name: "Bend", zips: "97701, 97702, 97703" },
    { name: "Sisters", zips: "97759" },
    { name: "Prineville", zips: "97754" },
    { name: "Madras", zips: "97741" },
    { name: "Terrebonne", zips: "97760" },
    { name: "Culver", zips: "97734" },
    { name: "Powell Butte", zips: "97753" },
    { name: "Tumalo", zips: "97703" },
    { name: "Eagle Crest", zips: "97756" },
    { name: "Crooked River Ranch", zips: "97760" },
    { name: "La Pine", zips: "97739" },
  ],
  benefits: [
    { icon: "ri-computer-line", title: "Start 100% Online from Redmond", desc: "Complete your emergency or expedited passport order from anywhere in Central Oregon — no road trip to Seattle required." },
    { icon: "ri-truck-line", title: "Overnight Shipping from Redmond or Bend", desc: "Ship your documents from FedEx Office in Bend or The UPS Store in Redmond — both support overnight delivery for expedited submissions." },
    { icon: "ri-shield-check-line", title: "Expert Document Review", desc: "Every application reviewed before submission — essential for Central Oregon residents 320 miles from the nearest regional passport agency." },
    { icon: "ri-calendar-check-line", title: "Reserved Agency Submission Access", desc: "We bypass the public appointment backlog with reserved Seattle Passport Agency slots — far faster than calling for an emergency appointment." },
  ],
  testimonials: [
    {
      name: "David M.",
      location: "Redmond, OR",
      rating: 5,
      text: "I had a tech conference in Tokyo and my passport was expired. Two weeks out from Redmond — panicking. Fast Passport Center renewed it in 7 days. They walked me through the overnight shipping from FedEx in Bend, kept me updated every day. Made the flight from RDM. Incredible service for Central Oregon.",
      service: "Expedited Passport Renewal",
    },
    {
      name: "Jennifer R.",
      location: "Sisters, OR",
      rating: 5,
      text: "We booked a New Zealand hiking adventure and realized my husband's passport expired during COVID. Fast Passport Center got it done in 5 business days from Sisters. Shipped overnight from Bend FedEx. We fly out of Redmond RDM to Seattle and on to Auckland. Thank you!",
      service: "Expedited Passport Renewal",
    },
    {
      name: "Tom A.",
      location: "Prineville, OR",
      rating: 5,
      text: "Needed an emergency passport for a family emergency in Mexico. Fast Passport Center did it in 3 business days from Prineville. They guided me through the Crook County Clerk acceptance step and expedited from there. Made the flight from RDM. Highly recommend for any Central Oregon rural resident.",
      service: "Emergency Passport Processing",
    },
  ],
  facilities: [
    {
      id: "redmond-post-main",
      name: "Redmond Post Office",
      type: "USPS Passport Acceptance",
      address: "618 NW Hemlock Ave",
      city: "Redmond",
      state: "OR",
      zip: "97756",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–5pm",
      distance: "Downtown Redmond — primary passport acceptance for Redmond and north Deschutes County",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Primary passport acceptance facility for Redmond and north Deschutes County residents. Appointment required. Availability can be limited during high tourist season in Central Oregon.",
      icon: "ri-mail-line",
    },
    {
      id: "deschutes-county-clerk",
      name: "Deschutes County Clerk — Bend Office",
      type: "County Clerk Passport Acceptance",
      address: "1300 NW Wall St, Suite 200",
      city: "Bend",
      state: "OR",
      zip: "97703",
      phone: "(541) 388-6546",
      hours: "Mon–Fri 8am–5pm",
      distance: "Bend — about 17 miles south via US-97",
      website: "https://www.deschutes.org/clerk",
      websiteLabel: "Deschutes County Clerk",
      notes: "Deschutes County Clerk in Bend accepts passport applications for all Deschutes County residents. Often better appointment availability than post offices, especially in tourist season.",
      icon: "ri-government-line",
    },
    {
      id: "bend-post-main",
      name: "Bend Post Office",
      type: "USPS Passport Acceptance",
      address: "2300 NE 4th St",
      city: "Bend",
      state: "OR",
      zip: "97701",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–5pm",
      distance: "Bend — about 17 miles south via US-97",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Bend's main post office serves south Deschutes County. Appointment required. High demand during tourist season — book 2+ weeks ahead.",
      icon: "ri-mail-line",
    },
    {
      id: "sisters-post",
      name: "Sisters Post Office",
      type: "USPS Passport Acceptance",
      address: "694 N Larch St",
      city: "Sisters",
      state: "OR",
      zip: "97759",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "Sisters — about 20 miles northwest via US-20",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Small facility serving the Sisters area. Very limited appointment availability. Redmond or Bend offices strongly preferred.",
      icon: "ri-mail-line",
    },
    {
      id: "madras-post",
      name: "Madras Post Office",
      type: "USPS Passport Acceptance",
      address: "71 SE D St",
      city: "Madras",
      state: "OR",
      zip: "97741",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4:30pm",
      distance: "Madras — about 26 miles north via US-97",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Alternative facility for north Jefferson County residents near Redmond. Smaller office but often better appointment availability.",
      icon: "ri-mail-line",
    },
    {
      id: "crook-county-clerk",
      name: "Crook County Clerk's Office",
      type: "County Clerk Passport Acceptance",
      address: "300 NE 3rd St",
      city: "Prineville",
      state: "OR",
      zip: "97754",
      phone: "(541) 447-6553",
      hours: "Mon–Fri 8am–5pm",
      distance: "Prineville — about 20 miles east via OR-126",
      website: "https://co.crook.or.us/clerk",
      websiteLabel: "Crook County Clerk",
      notes: "Crook County Clerk in Prineville serves east Deschutes County and Crook County residents. Generally good appointment availability.",
      icon: "ri-government-line",
    },
  ],
  faqs: [
    {
      q: "Can I get an emergency passport from Redmond, Oregon?",
      a: "Yes. Fast Passport Center handles emergency passport processing for Redmond residents as fast as 24 hours. The Seattle Passport Agency is about 5.5 hours from Redmond — we serve you entirely remotely via overnight shipping from FedEx Office in Bend or The UPS Store in Redmond. Start online, we handle the rest.",
    },
    {
      q: "How fast can I get an expedited passport from Redmond, OR?",
      a: "Expedited passport processing from Redmond takes 2–9 business days through Fast Passport Center. Most adult renewals don't require an in-person acceptance visit — ship overnight from FedEx Office in Bend or The UPS Store in Redmond.",
    },
    {
      q: "Where is the passport acceptance office in Redmond?",
      a: "Redmond's primary passport acceptance location is the Redmond Post Office (618 NW Hemlock Ave) in downtown Redmond. Additional options include the Deschutes County Clerk in Bend (17 miles south), the Bend Post Office, and the Sisters Post Office (20 miles northwest). The Crook County Clerk in Prineville (20 miles east) is also available.",
    },
    {
      q: "Does Roberts Field (RDM) airport in Redmond affect passport timing?",
      a: "Roberts Field (RDM) serves Redmond and all of Central Oregon with connections to major U.S. hubs (Seattle, Portland, Denver, San Francisco, Salt Lake City, Phoenix). RDM makes international travel accessible from Central Oregon — passengers connect through these hubs to destinations worldwide. Fast Passport Center handles expedited passports for Redmond RDM travelers.",
    },
    {
      q: "How far is Redmond from the Seattle Passport Agency?",
      a: "Redmond is approximately 320 miles from the Seattle Passport Agency — about 5.5 hours by car via US-97 north and I-5. For life-or-death emergencies requiring same-day processing, a Seattle trip may be necessary. For most urgent travel needs (2–9 business days), Fast Passport Center serves Redmond entirely remotely.",
    },
    {
      q: "What airport do Redmond residents use for international travel?",
      a: "Roberts Field (RDM) in Redmond is the primary airport for Central Oregon travelers. RDM offers connections through Seattle (SEA), Portland (PDX), Denver (DEN), San Francisco (SFO), and Salt Lake City (SLC) — providing access to international routes worldwide. Most Redmond international travelers connect through Seattle-Tacoma or Portland International.",
    },
    {
      q: "Is Redmond a popular tourist destination that affects passport services?",
      a: "Yes — Redmond and Central Oregon are major tourist destinations, especially in summer (Smith Rock, outdoor recreation, festivals) and winter (skiing at Mt. Bachelor/HooDoo). Peak tourist seasons mean acceptance facility appointments fill up faster. Plan acceptance appointments 2–3 weeks ahead during June–August.",
    },
    {
      q: "Can I renew my passport from Redmond without visiting an office?",
      a: "Yes. Most adult Redmond residents renewing their passport can do so entirely by mail — no in-person acceptance visit required for renewals. Ship overnight from FedEx Office Bend or The UPS Store Redmond, and we handle the Seattle agency submission.",
    },
    {
      q: "Can I get a child passport from Redmond?",
      a: "Yes. Child passports require an in-person acceptance visit at the Redmond Post Office, Deschutes County Clerk in Bend, or other acceptance facility — both parents must typically be present or provide notarized consent. Fast Passport Center provides the child passport checklist and expedites after the acceptance step.",
    },
    {
      q: "How much does expedited passport processing cost from Redmond?",
      a: "Fast Passport Center pricing includes the U.S. government passport fees plus our courier service fee — which is typically 30–100% lower than FedEx Office or retail passport services. We provide an exact price quote at checkout before any commitment.",
    },
  ],
  photoLocations: [
    { name: "Walgreens — Redmond", address: "1705 SW Odem Medo Rd, Redmond, OR 97756", hours: "Daily 8 AM – 10 PM" },
    { name: "The UPS Store — Redmond", address: "1722 SW Veterans Way, Redmond, OR 97756", hours: "Mon–Fri 8:30 AM – 6 PM, Sat 10 AM – 3 PM" },
    { name: "FedEx Office — Bend", address: "63465 N Hwy 97, Bend, OR 97703", hours: "Mon–Fri 7 AM – 10 PM, Sat–Sun 9 AM – 6 PM" },
    { name: "Walgreens — Bend (3rd St)", address: "223 NE 3rd St, Bend, OR 97701", hours: "Daily 8 AM – 10 PM" },
    { name: "Costco Photo Center — Bend", address: "2500 NE Hwy 20, Bend, OR 97701", hours: "Mon–Fri 10 AM – 8:30 PM, Sat–Sun 9:30 AM – 6 PM" },
    { name: "Fred Meyer — Redmond", address: "500 NW 7th St, Redmond, OR 97756", hours: "Daily 7 AM – 11 PM" },
    { name: "Safeway — Sisters", address: "450 N Cedar St, Sisters, OR 97759", hours: "Daily 5 AM – 11 PM" },
    { name: "Target Photo — Bend", address: "63455 N Hwy 97, Bend, OR 97703", hours: "Daily 8 AM – 10 PM" },
  ],
  dmvLocations: [
    {
      name: "Redmond Driver & Motor Vehicle Services",
      address: "215 SE 7th St, Redmond, OR 97756",
      hours: "Mon–Fri 8:30 AM – 5:00 PM (closed noon–1pm)",
      note: "Redmond DMV — driver licenses, REAL ID, and state ID cards for Redmond and north Deschutes County residents.",
      featured: true,
    },
    {
      name: "Bend Driver & Motor Vehicle Services",
      address: "222 NE Emerson Ave, Bend, OR 97701",
      hours: "Mon–Fri 8:30 AM – 5:00 PM",
      note: "Bend DMV — serves south Deschutes County and is the larger regional office.",
      featured: false,
    },
    {
      name: "Prineville Driver & Motor Vehicle Services",
      address: "365 NE Court St, Prineville, OR 97754",
      hours: "Mon–Fri 8:30 AM – 5:00 PM",
      note: "Prineville DMV — alternative for east Deschutes County and Crook County residents.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "RDM",
      name: "Roberts Field — Redmond Municipal Airport",
      distance: "Primary Central Oregon airport — located in Redmond, 2 miles from downtown",
      address: "2522 SE Jesse Butler Cir, Redmond, OR 97756",
      note: "Roberts Field (RDM) is Central Oregon's regional airport with daily connections to Seattle, Portland, Denver, San Francisco, Salt Lake City, Phoenix, and Los Angeles. International travel from Redmond connects through these major hubs — making RDM a critical gateway for Central Oregon international travel.",
      flightUrl: "https://www.flyrdm.com/",
      flightLabel: "RDM Airport Website",
      featured: true,
    },
    {
      code: "PDX",
      name: "Portland International Airport",
      distance: "About 145 miles northwest via US-26 (2.5–3 hours)",
      address: "7000 NE Airport Way, Portland, OR 97218",
      note: "Portland International is the alternative major hub for Central Oregon travelers who prefer direct international flights. PDX offers direct international routes to Amsterdam, London, Tokyo, Vancouver BC, and Mexico City.",
      flightUrl: "https://www.flypdx.com/",
      flightLabel: "PDX Airport Website",
      featured: false,
    },
    {
      code: "SEA",
      name: "Seattle-Tacoma International Airport",
      distance: "About 320 miles northwest via US-97 and I-5 (5.5 hours)",
      address: "17801 International Blvd, Seattle, WA 98158",
      note: "SEA-TAC is accessible but requires significant travel from Redmond. RDM connections through SEA are the practical option for most Central Oregon international travelers.",
      flightUrl: "https://www.portseattle.org/sea-tac",
      flightLabel: "SEA-TAC Airport Website",
      featured: false,
    },
  ],
  services: [
    {
      icon: "ri-time-line",
      title: "Emergency Passport — Redmond & Central Oregon",
      description: "RDM international connection imminent? Last-minute tech conference abroad? Family emergency overseas? Fast Passport Center handles emergency passport processing for Redmond and Central Oregon residents — as fast as 24 hours. Serve entirely remotely.",
      bullets: ["As fast as 24 hours processing", "Reserved Seattle agency submission", "Dedicated agent and live status tracking"],
      badge: "Same-Day Available",
      link: "https://fastpassportcenter.com/checkout/?passport=emergency-passport",
    },
    {
      icon: "ri-refresh-line",
      title: "Expedited Passport Renewal",
      description: "Passport expired during the pandemic and you're heading abroad from RDM? Redmond residents renew fast through Fast Passport Center — ship from FedEx Office Bend or The UPS Store Redmond overnight, we handle the expedited submission.",
      bullets: ["Fastest renewal for Central Oregon residents", "Ship overnight from Redmond or Bend", "Professional document review included"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-search-eye-line",
      title: "Lost Passport Replacement",
      description: "Lost your passport in Central Oregon or before an international trip? We guide Redmond travelers through DS-64 and expedited replacement — no Seattle trip required.",
      bullets: ["DS-64 theft/loss reporting guidance", "Expedited replacement timelines available", "Document review included"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport — Redmond Families",
      description: "Central Oregon families heading to BC, Mexico, Europe, or Asia need child passports. We guide you through the Redmond Post Office or Deschutes County Clerk acceptance step.",
      bullets: ["Both parents typically required to consent", "Redmond Post Office or Bend County Clerk guidance", "Expedited after in-person acceptance step"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-add-line",
      title: "First-Time Passport Applications",
      description: "First passport from Redmond? In-person acceptance at the Redmond Post Office or Deschutes County Clerk is required for first-timers. We guide you through every step and expedite from there.",
      bullets: ["Step-by-step acceptance guidance for Redmond and Bend facilities", "Photo location guide for Redmond and Central Oregon area", "Expedited processing after clerk visit"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-truck-line",
      title: "Registered State Dept. Courier Service",
      description: "Fast Passport Center is a registered U.S. Department of State courier — not a reseller. We submit through reserved agency channels at 30–100% lower prices than retail services.",
      bullets: ["State Dept. registered courier", "Best price guarantee", "Not affected by government shutdowns"],
      badge: "Registered Courier",
      link: "https://fastpassportcenter.com/checkout/",
    },
    {
      icon: "ri-edit-line",
      title: "Name Change & Specialty Passport Services",
      description: "Name change passports, damaged passport replacement, second passports for frequent business travelers, tech professionals, and outdoor adventure guides in the Central Oregon area.",
      bullets: ["Name change after marriage", "Damaged passport replacement", "Second passport for frequent business travelers"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Same-day emergency passport processing for Redmond, Oregon residents with urgent international travel needs." },
    { name: "Expedited Passport", description: "Expedited passport processing for Redmond and Central Oregon residents in 2–9 business days." },
    { name: "Passport Renewal", description: "Fast passport renewal for Redmond, Oregon — ship overnight from FedEx Office Bend or The UPS Store Redmond." },
    { name: "Lost Passport Replacement", description: "Emergency passport replacement for Redmond travelers with lost or stolen passports." },
    { name: "Child Passport", description: "Expedited child passport services for Redmond and Deschutes County families." },
    { name: "Passport Courier Service", description: "Registered U.S. Department of State courier serving Redmond, Oregon and Central Oregon." },
  ],
  mistakes: [
    { icon: "ri-time-line", title: "Booking RDM International Connections Without Checking Passport Validity", description: "Redmond residents booking international travel through RDM connections (to Seattle, Portland, Denver, SFO, SLC) often discover passport issues after booking — RDM makes international travel deceptively easy and people forget the passport check." },
    { icon: "ri-camera-off-line", title: "Submitting Poor-Quality Passport Photos", description: "Photos from drugstores or self-taken phone photos frequently get rejected for lighting or expression issues. Fast Passport Center reviews photo specifications before submission." },
    { icon: "ri-file-damage-line", title: "Incomplete Forms or Missing Signatures", description: "One missing signature returns your application — costing weeks when you're 320 miles from Seattle. Our pre-submission review catches all errors before delays happen." },
    { icon: "ri-pen-nib-line", title: "Wrong Birth Certificate Format", description: "Short-form birth certificates are rejected. We specify exactly what document format is needed based on your state of birth." },
    { icon: "ri-calendar-close-line", title: "Underestimating Central Oregon's Distance from Passport Agencies", description: "Redmond is 320 miles from the Seattle Passport Agency — a 5.5-hour drive through the high desert and over mountain passes. Last-minute agency runs are impractical. Use Fast Passport Center's overnight shipping service from day one." },
    { icon: "ri-global-line", title: "Outdoor Adventure and Tech Industry Last-Minute International Travel", description: "Central Oregon's growing tech and outdoor recreation industries generate international travel to conferences (Tokyo, Berlin, Amsterdam) and adventure tourism (New Zealand, Patagonia, Alps). These trips are often planned with short notice — check passport validity before booking." },
  ],
  steps: [
    { number: "01", icon: "ri-computer-line", title: "Start Your Order Online", description: "Choose your processing speed based on your Redmond travel date. Complete your secure order from home in minutes." },
    { number: "02", icon: "ri-file-list-3-line", title: "Receive Your Personalized Checklist", description: "We send a personalized document checklist — exactly what to gather, with Redmond and Central Oregon acceptance location guidance." },
    { number: "03", icon: "ri-send-plane-line", title: "Ship Documents Overnight", description: "Drop off at FedEx Office in Bend or The UPS Store in Redmond for overnight delivery. Both are reliable options for Central Oregon residents." },
    { number: "04", icon: "ri-building-2-line", title: "We Submit Through Reserved Agency Access", description: "We submit through reserved Seattle Passport Agency slots — bypassing the public appointment backlog that stretches for weeks." },
    { number: "05", icon: "ri-passport-line", title: "Passport Shipped to Your Door", description: "Your completed passport ships directly to your Redmond or Central Oregon address. Live status tracking included." },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Must drive to Seattle — 5.5 hours from Redmond",
        "Life-or-death emergencies only",
        "Very limited appointment availability",
        "Long wait times even with appointment",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Ship overnight from Redmond or Bend",
        "Document review included",
        "Dedicated agent on your case",
        "Daily status updates",
        "Best price guarantee",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than USPS self-service",
        "Document review included",
        "Dedicated agent",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "Standard mail — no courier access",
        "6–13 weeks; can be longer in summer",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent",
        "Not suitable if travel is under 10 weeks away",
      ],
      highlight: false,
      muted: true,
    },
  ],
  comparisonRows: [
    { feature: "Appointment Required", agency: "Yes — weeks-long wait, Seattle only", fpc: "No appointment needed to start" },
    { feature: "In-Person Visit", agency: "Drive to Seattle — 5.5 hours from Redmond", fpc: "Ship overnight from Redmond or Bend" },
    { feature: "Processing Speed", agency: "Strict eligibility requirements", fpc: "As fast as 24 hours — multiple tiers" },
    { feature: "Document Review", agency: "None — errors cause rejection", fpc: "Professional review included" },
    { feature: "Central Oregon Accessibility", agency: "Not designed for high desert residents", fpc: "Fully remote service via overnight shipping" },
    { feature: "Government Shutdowns", agency: "May be impacted", fpc: "Not affected" },
    { feature: "Best Price Guarantee", agency: "Govt. fees + 11-hour Seattle round trip cost", fpc: "Best price guarantee — no travel required" },
  ],
  stickyNavLinks: [
    { label: "Processing Options", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Acceptance Locations", href: "#acceptance-facilities" },
    { label: "vs. Regional Agency", href: "#comparison" },
    { label: "FAQ", href: "#faq" },
    { label: "Local Resources", href: "#local-resources" },
  ],
  internalLinks: [
    { name: "Expedited passport services in Bend", route: "/bend" },
    { name: "Emergency passport help in Portland", route: "/portland" },
    { name: "Fast passport renewal in Prineville", route: "/prineville" },
    { name: "Rush passport services for Oregon residents", route: "/oregon" },
    { name: "Expedited passports statewide in Oregon", route: "/oregon" },
  ],
  fpcOfficeNote: "After placing your order online, we send your personalized checklist and overnight shipping instructions. Ship from FedEx Office in Bend or The UPS Store in Redmond — both available weekday evenings. No visit to our processing facility required for most services.",
  fpcOfficeDistance: "Serving Redmond and all of Central Oregon remotely",
  serviceAreaIntro: "Whether you are in Redmond, Bend, Sisters, Prineville, Madras, La Pine, or anywhere in Deschutes County — Fast Passport Center makes emergency and expedited passport processing simple without a drive to Seattle.",
  nearbyCitiesIntro: "We serve all Central Oregon communities including Redmond, Bend, Sisters, Prineville, Madras, Terrebonne, Culver, Powell Butte, Tumalo, Eagle Crest, and Crooked River Ranch.",
  howItWorksIntro: "Getting an emergency or expedited passport from Redmond is simpler than a 5.5-hour drive to Seattle. Most Central Oregon clients never visit an office.",
  mistakesIntro: "These are the most common mistakes that cause delays for Redmond and Central Oregon passport applicants. We review your documents before submission to prevent all of them.",
  testimonialsIntro: "Real reviews from Redmond, Bend, Sisters, and Central Oregon travelers who needed their passports fast.",
  faqIntro: "The most common questions Redmond and Central Oregon residents ask about emergency and expedited passport services.",
  statePageRoute: "/states/oregon",
  statePageName: "Oregon",
  travelTiming: {
    title: "Travel & Passport Timing for Redmond and Central Oregon Residents",
    intro: "Redmond is Central Oregon's high desert hub — home to Roberts Field (RDM), the region's airport, and gateway to Smith Rock State Park, one of the world's premier climbing destinations. Redmond's growing tech sector, outdoor recreation culture, and airport access create unique international travel patterns for Deschutes County residents.",
    cards: [
      {
        icon: "ri-flight-takeoff-line",
        title: "Roberts Field (RDM) — Central Oregon's International Gateway",
        subtitle: "RDM connections make international travel accessible from the high desert",
        description: "Roberts Field (RDM) in Redmond is Central Oregon's regional airport with daily connections to major U.S. hubs — Seattle, Portland, Denver, San Francisco, Salt Lake City, and Phoenix. These connections make international travel surprisingly accessible from the high desert. RDM passengers connect through these hubs to destinations worldwide — Japan through Seattle, Europe through Denver or SFO, Mexico through Phoenix. Fast Passport Center handles expedited passports for Redmond RDM travelers.",
        tip: "RDM connections make international travel feel easy, but passport requirements don't change. Check passport expiration before booking any international connection through RDM — even if you're just connecting through Seattle. Renewals take 2–9 business days from Redmond.",
      },
      {
        icon: "ri-compass-3-line",
        title: "Outdoor Adventure Travel — New Zealand, Patagonia, Alps, Nepal",
        subtitle: "Central Oregon's outdoor culture creates international adventure passport demand",
        description: "Central Oregon's outdoor recreation culture — rock climbing at Smith Rock, skiing at Mt. Bachelor, mountain biking at Phil's Trail, fly fishing the Deschutes — creates a community of adventure travelers who take their passion international. New Zealand (Southern Alps hiking), Patagonia (Torres del Paine trekking), the European Alps (skiing and mountaineering), and Nepal (Everest region trekking) are top destinations. These trips are typically booked 3–6 months in advance, with passport discovery happening 4–8 weeks before departure.",
        tip: "Adventure travel companies often require passport details 90 days before departure for permits and reservations. Check your passport the day you commit to any international outdoor adventure. Expedited processing from Redmond handles the 2–9 business day window.",
      },
      {
        icon: "ri-computer-line",
        title: "Tech Industry & Remote Work International Travel",
        subtitle: "Central Oregon's growing tech community travels internationally for conferences and work",
        description: "Redmond and Bend have experienced significant tech industry growth — remote workers, startups, and established tech companies. This community travels internationally for conferences (Tokyo, Berlin, Amsterdam, London), client meetings, and team retreats. Tech conferences are scheduled months in advance but passport discovery often happens at the last minute — when registering or booking flights.",
        tip: "Tech professionals in Central Oregon should maintain passport validity of at least 6 months at all times. International conferences can arise with 4–6 weeks notice — ideal for expedited processing from Redmond.",
      },
      {
        icon: "ri-sun-line",
        title: "Mexico & Costa Rica Winter Escapes from the High Desert",
        subtitle: "Central Oregon winters are cold — tropical escapes are the antidote",
        description: "Central Oregon winters feature freezing temperatures and snow — creating strong demand for tropical winter escapes. Mexico (Puerto Vallarta, Cabo, Riviera Maya) and Costa Rica (Guanacaste, Arenal) are the top winter destinations for Redmond and Central Oregon residents. These trips are typically booked in October–November for January–March travel.",
        tip: "Check passport expiration dates in September before booking that Mexico winter escape. The 2–4 weeks between booking and departure is perfect for Fast Passport Center expedited processing (2–9 business days) from Redmond.",
      },
    ],
    callout: {
      icon: "ri-flight-takeoff-line",
      title: "Redmond, Oregon — Central Oregon's High Desert Hub",
      description: "Redmond sits at the intersection of high desert adventure, growing tech industry, and regional airport access. The city's unique identity — home to Roberts Field (RDM), gateway to Smith Rock, surrounded by the Cascade panorama — creates a distinctive set of international travel patterns. Fast Passport Center has served Redmond and Central Oregon residents with expedited passports for RDM international connections, outdoor adventure travel, tech conferences, and winter escapes.",
    },
  },
  destinations: {
    title: "Where Redmond Travelers Go — And Why Passports Get Urgent",
    intro: "These are the destinations and travel patterns we see most from Redmond and Central Oregon clients. RDM airport access, outdoor adventure culture, tech industry growth, and winter escape needs create unique passport urgency situations for Deschutes County residents.",
    stats: [
      { stat: "320 mi", label: "to Seattle Passport Agency — the closest regional passport office from Redmond" },
      { stat: "RDM", label: "Roberts Field in Redmond — Central Oregon's regional airport with connections to 6 major U.S. hubs" },
      { stat: "5.5 hrs", label: "to Seattle — Redmond's most distant major service from any regional passport agency" },
      { stat: "2–9", label: "business days for Fast Passport Center expedited processing from Redmond" },
    ],
    destinations: [
      { name: "Japan — Tokyo, Kyoto, Osaka", context: "Japanese tech conferences, outdoor adventure (Japanese Alps), and cultural travel connecting through Seattle or SFO from RDM", urgency: "Tech conferences scheduled 8–12 weeks ahead; passport renewal needed 4–6 weeks before departure", icon: "ri-global-line" },
      { name: "New Zealand — South Island Adventure", context: "Southern Alps hiking, Queenstown adventure, Milford Track — the #1 outdoor adventure destination for Central Oregon travelers", urgency: "Adventure tours require passport details 90 days before departure; renewals needed 10–12 weeks before travel", icon: "ri-compass-3-line" },
      { name: "Mexico — Puerto Vallarta, Cabo, Riviera Maya", context: "Top winter escape for Redmond residents: tropical beaches booked in October for January departure via RDM to Phoenix or SLC connections", urgency: "October–November booking; passport issues discovered 2–4 weeks before departure", icon: "ri-sun-cloudy-line" },
      { name: "British Columbia — Vancouver, Whistler", context: "Pacific Northwest neighbor accessible via RDM to Seattle connection — ski trips and weekend getaways", urgency: "BC ski season booked in November; summer trips booked in March — passport discovery at booking time", icon: "ri-map-pin-2-line" },
      { name: "Germany & Netherlands — Tech Conferences", context: "Berlin, Amsterdam, and Munich tech conferences for Central Oregon's growing tech workforce", urgency: "European tech conferences are scheduled months ahead; passport issues discovered at registration time", icon: "ri-computer-line" },
      { name: "Costa Rica — Eco-adventure & Surfing", context: "Guanacaste, Arenal, and Osa Peninsula eco-adventure and surf trips from Central Oregon's outdoor community", urgency: "November–January booking for February–April travel; expedition permits require passport 60 days ahead", icon: "ri-leaf-line" },
    ],
    crossLinksTitle: "Explore Nearby Oregon Service Areas",
    crossLinksDesc: "We serve travelers throughout Central Oregon and the high desert with the same expedited passport expertise.",
    crossLinks: [
      { name: "Bend", route: "/bend" },
      { name: "Prineville", route: "/prineville" },
      { name: "Portland", route: "/portland" },
    ],
  },
};